import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCheckout,
  createBillingPortal,
  getBillingCustomer,
  upgradePlan,
  downgradePlan,
  cancelPendingDowngrade,
  cancelSubscription,
  reactivateSubscription,
  type CreateCheckoutRequest,
  type PlanChangeRequest,
} from '../api/billingApi';

/**
 * Mutations for the billing journey (checkout + plan changes) against the
 * multi-provider Billing module. Each plan-changing mutation invalidates the
 * subscription-status query so the My Plan view reflects the webhook-driven
 * result. Provider-agnostic: callers redirect to `redirectUrl` from checkout.
 *
 * Branching rule (see frontend-checkout-scope.md): upgrade/downgrade require an
 * already-active paid subscription. Use `useBillingCustomer().data.providerSubscriptionId`
 * (or the subscription status) to choose checkout (first conversion) vs upgrade/downgrade.
 */

function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
    queryClient.invalidateQueries({ queryKey: ['subscription', 'usage'] });
  };
}

/** The org's provider/subscription linkage. 404 (no customer yet) is treated as "no data", not an error retry. */
export function useBillingCustomer() {
  return useQuery({
    queryKey: ['billing', 'customer'],
    queryFn: getBillingCustomer,
    retry: false,
  });
}

/**
 * Starts hosted checkout. The caller redirects on success, e.g.:
 *   const { redirectUrl } = await mutateAsync(req);
 *   window.location.href = redirectUrl;
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: (req: CreateCheckoutRequest) => createCheckout(req),
  });
}

export function useUpgradePlan() {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: (req: PlanChangeRequest) => upgradePlan(req),
    onSuccess: invalidate,
  });
}

export function useDowngradePlan() {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: (req: PlanChangeRequest) => downgradePlan(req),
    onSuccess: invalidate,
  });
}

export function useCancelDowngrade() {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: () => cancelPendingDowngrade(),
    onSuccess: invalidate,
  });
}

export function useCancelSubscription() {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: invalidate,
  });
}

export function useReactivateSubscription() {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: () => reactivateSubscription(),
    onSuccess: invalidate,
  });
}

/** Opens a provider-hosted management session (Stripe portal); caller redirects to `url`. */
export function useBillingPortal() {
  return useMutation({
    mutationFn: (returnUrl?: string) => createBillingPortal(returnUrl),
  });
}
